import React, { useState, useEffect } from "react";

import { motion, AnimatePresence } from "framer-motion";
import Chart from "react-apexcharts";
import { Responsive, WidthProvider } from "react-grid-layout";
import { useNavigate } from "react-router-dom";

import ClientActivityPanel from "../components/admin/ClientActivityPanel";
import ClientInsightsRow from "../components/admin/ClientInsightsRow";
import LiveUserMap from "../components/LiveUserMap";
import {
  AnimatedNumber,
  Shimmer,
  ListItemMotion,
  formatCurrency,
} from "../components/MotionPrimitives";
import StackedChart from "../components/StackedChart";
import Tooltip from "../components/Tooltip";
import { useAuth } from "../contexts/AuthContext";
import { useClientActivityGeo } from "../hooks/useClientActivityGeo";
import apiService from "../services/api";
import "react-grid-layout/css/styles.css";
import "react-resizable/css/styles.css";

const ResponsiveGridLayout = WidthProvider(Responsive);
const LIVE_HEARTBEAT_WINDOW_SECONDS = 120;
const LAST_HOUR_WINDOW_SECONDS = 60 * 60;

const CLIENT_INSIGHTS_LAYOUT = {
  i: "client-insights",
  x: 0,
  y: 6,
  w: 12,
  h: 5,
  minW: 8,
  minH: 5,
  maxH: 8,
};

const formatMetricWindow = (seconds, fallback) => {
  const value = Number(seconds);

  if (!Number.isFinite(value) || value <= 0) return fallback;
  if (value === LAST_HOUR_WINDOW_SECONDS) return "Last Hour";
  if (value % 60 === 0) return `Last ${value / 60} min`;

  return `Last ${value} sec`;
};

const MetricInfoTooltip = ({ text, horizontalAlign = "center" }) => (
  <Tooltip horizontalAlign={horizontalAlign} position="top" text={text}>
    <button
      aria-label="Metric definition"
      className="inline-flex h-4 w-4 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-primary-500/40 dark:text-gray-500 dark:hover:text-gray-300"
      type="button"
      onClick={(event) => event.stopPropagation()}
    >
      <i className="fas fa-info-circle text-[11px]" />
    </button>
  </Tooltip>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [assignedTickets, setAssignedTickets] = useState(0);
  const [awaitingReplyTickets, setAwaitingReplyTickets] = useState(0);
  const [ticketsData, setTicketsData] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isLandscape, setIsLandscape] = useState(
    window.innerHeight < window.innerWidth && window.innerWidth < 1024
  );
  // Default layouts for different breakpoints
  const defaultLayouts = {
    lg: [
      { i: "stat-active", x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 1, maxH: 3 },
      { i: "stat-users", x: 3, y: 0, w: 3, h: 2, minW: 2, minH: 1, maxH: 3 },
      { i: "stat-premium", x: 6, y: 0, w: 3, h: 2, minW: 2, minH: 1, maxH: 3 },
      { i: "stat-revenue", x: 9, y: 0, w: 3, h: 2, minW: 2, minH: 1, maxH: 3 },
      { i: "billing", x: 0, y: 2, w: 4, h: 4, minW: 4, minH: 3, maxH: 6 },
      { i: "activity", x: 4, y: 2, w: 4, h: 4, minW: 3, minH: 3, maxH: 6 },
      { i: "staff-online", x: 8, y: 2, w: 4, h: 4, minW: 4, minH: 3, maxH: 6 },
      CLIENT_INSIGHTS_LAYOUT,
      { i: "client-map", x: 0, y: 11, w: 12, h: 6, minW: 6, minH: 4, maxH: 10 },
      { i: "customers", x: 4, y: 11, w: 4, h: 4, minW: 2, minH: 3, maxH: 8 },
      { i: "tasks", x: 8, y: 11, w: 4, h: 4, minW: 2, minH: 2, maxH: 8 },
    ],
    md: [
      { i: "stat-active", x: 0, y: 0, w: 3, h: 2, minW: 2, minH: 1 },
      { i: "stat-users", x: 3, y: 0, w: 3, h: 2, minW: 2, minH: 1 },
      { i: "stat-premium", x: 0, y: 2, w: 3, h: 2, minW: 2, minH: 1 },
      { i: "stat-revenue", x: 3, y: 2, w: 3, h: 2, minW: 2, minH: 1 },
      { i: "revenue", x: 0, y: 4, w: 6, h: 5, minW: 4, minH: 4 },
      { i: "categories", x: 0, y: 9, w: 6, h: 5, minW: 3, minH: 4 },
      { i: "client-map", x: 0, y: 14, w: 6, h: 6, minW: 4, minH: 5 },
      { i: "activity", x: 0, y: 20, w: 6, h: 4, minW: 3, minH: 3 },
      { i: "performance", x: 0, y: 24, w: 6, h: 3, minW: 3, minH: 2 },
      { i: "customers", x: 0, y: 27, w: 6, h: 3, minW: 3, minH: 3 },
      { i: "tasks", x: 0, y: 30, w: 6, h: 3, minW: 2, minH: 3 },
    ],
    sm: [
      {
        i: "stat-active",
        x: 0,
        y: 0,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      {
        i: "stat-users",
        x: 2,
        y: 0,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      {
        i: "stat-premium",
        x: 0,
        y: 2,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      {
        i: "stat-revenue",
        x: 2,
        y: 2,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      { i: "billing", x: 0, y: 4, w: 4, h: 4, minW: 4, minH: 3, static: true },
      {
        i: "revenue",
        x: 0,
        y: 8,
        w: 4,
        h: 5,
        minW: 4,
        minH: 4,
        static: true,
      },
      {
        i: "categories",
        x: 0,
        y: 13,
        w: 4,
        h: 5,
        minW: 4,
        minH: 4,
        static: true,
      },
      {
        i: "client-map",
        x: 0,
        y: 18,
        w: 4,
        h: 5,
        minW: 4,
        minH: 5,
        static: true,
      },
      {
        i: "staff-online",
        x: 0,
        y: 23,
        w: 4,
        h: 3,
        minW: 4,
        minH: 3,
        static: true,
      },
      {
        i: "activity",
        x: 0,
        y: 26,
        w: 4,
        h: 5,
        minW: 4,
        minH: 3,
        static: true,
      },
      {
        i: "performance",
        x: 0,
        y: 31,
        w: 4,
        h: 3,
        minW: 4,
        minH: 3,
        static: true,
      },
      {
        i: "customers",
        x: 0,
        y: 34,
        w: 4,
        h: 3,
        minW: 4,
        minH: 3,
        static: true,
      },
      { i: "tasks", x: 0, y: 37, w: 4, h: 3, minW: 4, minH: 3, static: true },
    ],
    xs: [
      {
        i: "stat-active",
        x: 0,
        y: 0,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      {
        i: "stat-users",
        x: 2,
        y: 0,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      {
        i: "stat-premium",
        x: 0,
        y: 2,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      {
        i: "stat-revenue",
        x: 2,
        y: 2,
        w: 2,
        h: 2,
        minW: 2,
        minH: 2,
        maxW: 2,
        maxH: 2,
        static: true,
      },
      { i: "billing", x: 0, y: 4, w: 4, h: 4, minW: 4, minH: 3, static: true },
      {
        i: "revenue",
        x: 0,
        y: 8,
        w: 4,
        h: 5,
        minW: 4,
        minH: 4,
        static: true,
      },
      {
        i: "categories",
        x: 0,
        y: 13,
        w: 4,
        h: 5,
        minW: 4,
        minH: 4,
        static: true,
      },
      {
        i: "client-map",
        x: 0,
        y: 18,
        w: 4,
        h: 5,
        minW: 4,
        minH: 5,
        static: true,
      },
      {
        i: "staff-online",
        x: 0,
        y: 23,
        w: 4,
        h: 3,
        minW: 4,
        minH: 3,
        static: true,
      },
      {
        i: "activity",
        x: 0,
        y: 26,
        w: 4,
        h: 5,
        minW: 4,
        minH: 3,
        static: true,
      },
      {
        i: "performance",
        x: 0,
        y: 31,
        w: 4,
        h: 3,
        minW: 4,
        minH: 3,
        static: true,
      },
      {
        i: "customers",
        x: 0,
        y: 34,
        w: 4,
        h: 3,
        minW: 4,
        minH: 3,
        static: true,
      },
      { i: "tasks", x: 0, y: 37, w: 4, h: 3, minW: 4, minH: 3, static: true },
    ],
  };

  const [layouts, setLayouts] = useState(() => {
    // Always use default layouts to ensure correct positioning
    localStorage.removeItem("dashboardLayouts");
    // Force clear any cached layouts
    sessionStorage.removeItem("dashboardLayouts");

    return defaultLayouts;
  });

  // Force reset layouts on mount to ensure consistent heights
  useEffect(() => {
    setLayouts(defaultLayouts);
  }, []);

  const [gridBreakpoint, setGridBreakpoint] = useState("lg");
  const [isDragging, setIsDragging] = useState(false);
  const [hiddenWidgets] = useState(() => {
    const saved = localStorage.getItem("hiddenWidgets");

    return saved ? JSON.parse(saved) : [];
  });
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [dashboardStats, setDashboardStats] = useState({
    activeUsers: { count: 0, percentage: 0 },
    liveAppClients: { count: 0, windowSeconds: LIVE_HEARTBEAT_WINDOW_SECONDS },
    users: { totalCount: 0 },
    registrations: { totalCount: 0, premiumCount: 0 },
    transactions: {
      lifetime: { total: "0.00" },
      daily: { total: "0.00" },
      monthly: { total: "0.00" },
      yearly: { total: "0.00" },
    },
  });
  const [clientActivity, setClientActivity] = useState({
    activeClients: 0,
    authenticatedClients: 0,
    liveAppClients: 0,
    liveWindowSeconds: LIVE_HEARTBEAT_WINDOW_SECONDS,
    noAuthClients: 0,
    recentActivity: [],
    registeredAccountsOnline: 0,
  });
  const [clientActivityLoading, setClientActivityLoading] = useState(true);
  const [billingData, setBillingData] = useState({
    today: 0,
    thisMonth: 0,
    thisYear: 0,
    allTime: 0,
  });
  const [billingLoading, setBillingLoading] = useState(true);
  const [staffOnline, setStaffOnline] = useState([]);
  const [staffOnlineLoading, setStaffOnlineLoading] = useState(true);
  const { geoJson: clientActivityGeoJson, loading: clientMapLoading } =
    useClientActivityGeo(user?.role === "admin");

  // Handle window resize for mobile detection and layout refresh
  const [layoutKey, setLayoutKey] = useState(0);

  useEffect(() => {
    let resizeTimeout;

    const handleResize = () => {
      // Debounce resize events
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const newIsMobile = window.innerWidth < 768;
        const newIsLandscape =
          window.innerHeight < window.innerWidth && window.innerWidth < 1024;

        // Only update if values actually changed
        if (newIsMobile !== isMobile || newIsLandscape !== isLandscape) {
          setIsMobile(newIsMobile);
          setIsLandscape(newIsLandscape);
          // Force layout refresh by updating the key
          setLayoutKey((prev) => prev + 1);
          // Reset layouts to default for new breakpoint
          setLayouts(defaultLayouts);
        }
      }, 150);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("orientationchange", handleResize);

    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("orientationchange", handleResize);
    };
  }, [isMobile, isLandscape]);

  // Fetch dashboard stats from API instead of localStorage
  useEffect(() => {
    if (!user || user.role !== "admin") {
      setStatsLoading(false);

      return;
    }

    let isMounted = true;

    const fetchDashboardStats = async (isBackgroundUpdate = false) => {
      if (!isMounted) return;

      // Only show loading on initial fetch
      if (!isBackgroundUpdate) {
        setStatsLoading(true);
      }

      try {
        const response = await apiService.getDashboardStats();

        if (isMounted && response.status === 200) {
          setDashboardStats(response.data);
        }
      } catch (error) {
        console.error("Failed to fetch dashboard stats:", error);
        // Keep the default empty values on error
      } finally {
        if (isMounted && !isBackgroundUpdate) {
          setStatsLoading(false);
        }
      }
    };

    // Initial fetch with loading indicator
    fetchDashboardStats(false);

    // Refresh stats every 30 seconds in the background
    const interval = setInterval(() => fetchDashboardStats(true), 30000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [user]);

  // Fetch client activity data
  useEffect(() => {
    if (user?.role !== "admin") {
      setClientActivityLoading(false);

      return;
    }

    let cancelled = false;

    const fetchClientActivity = async () => {
      try {
        const response = await apiService.getClientActivity();

        if (cancelled) return;
        const data = response?.data || {};

        setClientActivity({
          activeClients:
            data.liveAppClients ??
            data.activeClients ??
            data.livePlaylistUsers ??
            0,
          authenticatedClients:
            data.authenticatedClients ?? data.livePlaylistUsers ?? 0,
          liveAppClients:
            data.liveAppClients ??
            data.activeClients ??
            data.livePlaylistUsers ??
            0,
          liveWindowSeconds:
            data.liveWindowSeconds ?? LIVE_HEARTBEAT_WINDOW_SECONDS,
          noAuthClients: data.noAuthClients ?? 0,
          recentActivity: Array.isArray(data.recentActivity)
            ? data.recentActivity
            : [],
          registeredAccountsOnline:
            data.registeredAccountsOnline ?? data.usersOnline ?? 0,
        });
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to load client activity", err);
        }
      } finally {
        if (!cancelled) {
          setClientActivityLoading(false);
        }
      }
    };

    fetchClientActivity();
    const intervalId = setInterval(fetchClientActivity, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [user]);

  // Update billing data from cached dashboardStats
  // No API calls needed - everything comes from the single getDashboardStats call
  useEffect(() => {
    if (!user || user.role !== "admin") return;

    if (!dashboardStats.transactions) {
      setBillingLoading(true);

      return;
    }

    setBillingLoading(false);

    const dailyTotal = parseFloat(
      dashboardStats.transactions.daily?.total || 0
    );
    const monthlyTotal = parseFloat(
      dashboardStats.transactions.monthly?.total || 0
    );
    const yearlyTotal = parseFloat(
      dashboardStats.transactions.yearly?.total || 0
    );
    const lifetimeTotal = parseFloat(
      dashboardStats.transactions.lifetime?.total || 0
    );

    setBillingData({
      today: dailyTotal,
      thisMonth: monthlyTotal,
      thisYear: yearlyTotal,
      allTime: lifetimeTotal,
    });
  }, [user?.role, dashboardStats.transactions]);

  // Track user activity
  useEffect(() => {
    const updateActivity = () => {
      const now = Date.now();

      // Store in localStorage so it persists across components
      localStorage.setItem("lastActivityTime", now.toString());

      // Send activity update to backend if needed
      if (user && user.id) {
        // Optional: Send activity ping to backend
        // apiService.updateUserActivity().catch(() => {});
      }
    };

    // Events that count as activity
    const activityEvents = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
      "keydown",
    ];

    // Throttle activity updates to once every 5 seconds
    let activityTimeout;
    const throttledUpdateActivity = () => {
      if (!activityTimeout) {
        updateActivity();
        activityTimeout = setTimeout(() => {
          activityTimeout = null;
        }, 5000); // Only update once every 5 seconds
      }
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledUpdateActivity);
    });

    // Only set initial activity time on first visit (no stored time)
    if (!localStorage.getItem("lastActivityTime")) {
      updateActivity();
    }

    // Cleanup
    return () => {
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledUpdateActivity);
      });
      if (activityTimeout) {
        clearTimeout(activityTimeout);
      }
    };
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    const mapStaff = (admin) => {
      const fullName =
        `${admin.firstName || ""} ${admin.lastName || ""}`.trim();
      const usernameFallback = (admin.email || "").split("@")[0];

      return {
        activity: admin.activity || {
          ticketsAnswered: 0,
          ticketsCreated: 0,
          publicReplies: 0,
        },
        id: admin.adminId,
        name: fullName || usernameFallback,
        username: usernameFallback,
        email: admin.email,
        avatar: admin.avatarUrl || null,
        isOnline: true,
        lastActive: admin.lastSeen,
      };
    };

    const fetchStaffOnline = async () => {
      try {
        const response = await apiService.getStaffOnline();

        if (cancelled) return;
        const list = (response?.data?.staffOnline || []).map(mapStaff);

        setStaffOnline(list);
      } catch (err) {
        if (!cancelled) {
          console.warn("Failed to load staff online", err);
        }
      } finally {
        if (!cancelled) {
          setStaffOnlineLoading(false);
        }
      }
    };

    fetchStaffOnline();
    const intervalId = setInterval(fetchStaffOnline, 30000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, []);

  // Fetch ticket count and assigned tickets
  useEffect(() => {
    if (!user) return;

    let isMounted = true;
    const fetchTicketCount = async () => {
      if (!isMounted) return;

      try {
        // Fetch tickets using the API service - only fetch if component is still mounted
        if (!isMounted) return;
        const data = await apiService.getTickets({ limit: 100 });

        // Count tickets assigned to current user and awaiting reply
        let assignedCount = 0;
        let awaitingReplyCount = 0;
        const recentTicketsList = [];

        if (data.tickets && Array.isArray(data.tickets) && user) {
          const userEmail = user.email?.toLowerCase();
          const userId = user.id;

          // Sort tickets by created date (most recent first)
          const sortedTickets = [...data.tickets].sort((a, b) => {
            const dateA = new Date(a.created_at || a.createdAt || 0);
            const dateB = new Date(b.created_at || b.createdAt || 0);

            return dateB - dateA;
          });

          // Get first 3 tickets for preview (to show in Support widget on mobile)
          const previewTickets = sortedTickets.slice(0, 3);

          previewTickets.forEach((ticket) => {
            const createdAt = new Date(ticket.created_at || ticket.createdAt);
            const now = new Date();
            const diffMs = now - createdAt;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);
            const diffDays = Math.floor(diffMs / 86400000);

            let timeAgo;

            if (diffMins < 60) {
              timeAgo = `${diffMins} ${diffMins === 1 ? "minute" : "minutes"} ago`;
            } else if (diffHours < 24) {
              timeAgo = `${diffHours} ${diffHours === 1 ? "hour" : "hours"} ago`;
            } else {
              timeAgo = `${diffDays} ${diffDays === 1 ? "day" : "days"} ago`;
            }

            // Extract customer name from ticket
            const customerName =
              ticket.customer_name ||
              ticket.customerName ||
              ticket.user?.name ||
              ticket.customer?.name ||
              ticket.created_by ||
              "Unknown User";

            recentTicketsList.push({
              id: ticket.id || ticket.ticket_id,
              ticketNumber:
                ticket.ticket_number || ticket.ticketNumber || ticket.id,
              subject: ticket.subject || ticket.title || "No subject",
              customerName,
              timeAgo,
            });
          });

          const assignedTickets = data.tickets
            .filter((ticket) => {
              // Check if ticket is assigned to current user
              const assignedTo = ticket.assignedTo || ticket.assigned_to;

              if (!assignedTo) return false;

              // Match by unique user ID or email, not by role name
              let isAssignedToCurrentUser = false;

              if (typeof assignedTo === "object" && assignedTo) {
                // Check all possible ID and email fields
                isAssignedToCurrentUser =
                  assignedTo.id === userId ||
                  assignedTo.email?.toLowerCase() === userEmail;
              } else if (typeof assignedTo === "string") {
                // Only match if it's an email
                if (assignedTo.includes("@")) {
                  isAssignedToCurrentUser =
                    assignedTo.toLowerCase() === userEmail;
                }
              }

              return isAssignedToCurrentUser;
            })
            .filter((ticket) => {
              // Only count ACTIVE tickets (exclude closed)
              // Dashboard should show tickets that need attention
              const status =
                ticket.status?.toLowerCase().replace("_", "-") || "";

              return status !== "closed";
            });

          assignedCount = assignedTickets.length;

          // Count tickets awaiting reply
          const awaitingTickets = data.tickets.filter((ticket) => {
            const status = ticket.status?.toLowerCase().replace("_", "-") || "";

            return status === "open" || status === "customer-replied";
          });

          awaitingReplyCount = awaitingTickets.length;
        }

        if (isMounted) {
          setAssignedTickets(assignedCount);
          setAwaitingReplyTickets(awaitingReplyCount);
          setTicketsData(recentTicketsList);
        }
      } catch (error) {
        if (isMounted) {
          console.error("Failed to fetch ticket count:", error);
          setAwaitingReplyTickets(0);
          setTicketsData([]);
        }
      }
    };

    // Initial fetch
    fetchTicketCount();

    // Refresh every 60 seconds (reduced from 30 to minimize API calls)
    const interval = setInterval(fetchTicketCount, 60000);

    const timer = setTimeout(() => {
      if (isMounted) {
        setLoading(false);
      }
    }, 1500);

    return () => {
      isMounted = false;
      clearInterval(interval);
      clearTimeout(timer);
    };
  }, [user?.id]); // Only re-run if user ID changes, not the whole user object

  // Widget definitions
  const widgets = [
    {
      id: "stat-active",
      name: "Live App Clients",
      icon: "fas fa-user-check",
      minW: 2,
      minH: 1,
    },
    {
      id: "stat-users",
      name: "Total Users",
      icon: "fas fa-users",
      minW: 2,
      minH: 1,
    },
    {
      id: "stat-premium",
      name: "Premium Users",
      icon: "fas fa-crown",
      minW: 2,
      minH: 1,
    },
    {
      id: "stat-revenue",
      name: "Total Resellers",
      icon: "fas fa-handshake",
      minW: 2,
      minH: 1,
    },
    {
      id: "client-insights",
      name: "Client Insights",
      icon: "fas fa-chart-line",
      minW: 8,
      minH: 5,
    },
    {
      id: "revenue",
      name: "Revenue Chart",
      icon: "fas fa-dollar-sign",
      minW: 4,
      minH: 3,
    },
    {
      id: "categories",
      name: "Client Activity",
      icon: "fas fa-users",
      minW: 2,
      minH: 3,
    },
    {
      id: "client-map",
      name: "Live Client Map",
      icon: "fas fa-map-marked-alt",
      minW: 4,
      minH: 4,
    },
    {
      id: "activity",
      name: "Support",
      icon: "fas fa-headset",
      minW: 3,
      minH: 3,
    },
    {
      id: "performance",
      name: "Performance Metrics",
      icon: "fas fa-tachometer-alt",
      minW: 3,
      minH: 2,
    },
    {
      id: "customers",
      name: "Top Customers",
      icon: "fas fa-users",
      minW: 3,
      minH: 3,
    },
    { id: "tasks", name: "My Tasks", icon: "fas fa-tasks", minW: 2, minH: 3 },
    {
      id: "billing",
      name: "Billing",
      icon: "fas fa-file-invoice-dollar",
      minW: 4,
      minH: 3,
    },
    {
      id: "staff-online",
      name: "Staff Online",
      icon: "fas fa-user-shield",
      minW: 4,
      minH: 3,
    },
  ];

  const liveAppClients = dashboardStats.liveAppClients || {};
  const legacyActiveUsers = dashboardStats.activeUsers || {};
  const liveClientSummary = clientActivityGeoJson?.summary || null;
  const liveAppClientCount =
    liveClientSummary?.total ??
    liveAppClients.count ??
    legacyActiveUsers.count ??
    0;
  const liveAppClientWindow =
    clientActivityGeoJson?.windowSeconds ??
    liveAppClients.windowSeconds ??
    legacyActiveUsers.windowSeconds ??
    LIVE_HEARTBEAT_WINDOW_SECONDS;
  const liveAppClientWindowLabel = formatMetricWindow(
    liveAppClientWindow,
    "Last 2 min"
  );
  const alignedClientActivity = {
    ...clientActivity,
    activeClients: liveClientSummary?.total ?? clientActivity.liveAppClients,
    authenticatedClients:
      liveClientSummary?.authenticated ?? clientActivity.authenticatedClients,
    liveAppClients: liveClientSummary?.total ?? clientActivity.liveAppClients,
    liveWindowSeconds:
      clientActivityGeoJson?.windowSeconds ?? clientActivity.liveWindowSeconds,
    noAuthClients: liveClientSummary?.noAuth ?? clientActivity.noAuthClients,
  };

  // Use real data from API
  const stats = [
    {
      title: "Live App Clients",
      value: statsLoading ? "..." : String(liveAppClientCount).toLocaleString(),
      rawValue: liveAppClientCount,
      tooltip: `Unique app clients with a heartbeat in the ${liveAppClientWindowLabel.toLowerCase()} window. Includes authenticated app users and no-auth app clients from the live heartbeat cache.`,
      icon: "fas fa-user-check",
      iconBg: "bg-green-500",
      iconHalo:
        "bg-green-500/10 ring-1 ring-green-500/25 text-green-600 dark:text-green-400",
      accent: "from-green-500/40",
      trend: liveAppClientCount > 0 ? "up" : "neutral",
      link: "/customers",
      animKey: "liveAppClients",
    },
    {
      title: "Total Users",
      value: statsLoading
        ? "..."
        : String(dashboardStats.users?.totalCount || 0).toLocaleString(),
      rawValue: dashboardStats.users?.totalCount || 0,
      change: statsLoading
        ? "Loading..."
        : `${dashboardStats.registrations?.totalCount || 0} registered`,
      tooltip:
        "Total rows in the users table. These are playlist/provider user records, not the registered billing customer count.",
      icon: "fas fa-users",
      iconBg: "bg-blue-500",
      iconHalo:
        "bg-blue-500/10 ring-1 ring-blue-500/25 text-blue-600 dark:text-blue-400",
      accent: "from-blue-500/40",
      trend: dashboardStats.users?.totalCount > 0 ? "up" : "neutral",
      link: "/users",
      animKey: "totalUsers",
    },
    {
      title: "Premium Users",
      value: statsLoading
        ? "..."
        : String(
            dashboardStats.registrations?.premiumCount || 0
          ).toLocaleString(),
      rawValue: dashboardStats.registrations?.premiumCount || 0,
      change: statsLoading
        ? "Loading..."
        : `${(
            ((dashboardStats.registrations?.premiumCount || 0) /
              Math.max(1, dashboardStats.registrations?.totalCount || 1)) *
            100
          ).toFixed(1)}% of registered`,
      icon: "fas fa-crown",
      iconBg: "bg-purple-500",
      iconHalo:
        "bg-purple-500/10 ring-1 ring-purple-500/25 text-purple-600 dark:text-purple-400",
      accent: "from-purple-500/40",
      trend: dashboardStats.registrations?.premiumCount > 0 ? "up" : "neutral",
      link: "/customers?type=Premium",
      animKey: "premiumUsers",
    },
    {
      title: "Total Resellers",
      value: statsLoading
        ? "..."
        : String(
            dashboardStats.registrations?.resellersCount || 0
          ).toLocaleString(),
      rawValue: dashboardStats.registrations?.resellersCount || 0,
      change: statsLoading
        ? "Loading..."
        : `${(
            ((dashboardStats.registrations?.resellersCount || 0) /
              Math.max(1, dashboardStats.registrations?.totalCount || 1)) *
            100
          ).toFixed(1)}% of registered`,
      icon: "fas fa-handshake",
      iconBg: "bg-orange-500",
      iconHalo:
        "bg-orange-500/10 ring-1 ring-orange-500/25 text-orange-600 dark:text-orange-400",
      accent: "from-orange-500/40",
      trend:
        dashboardStats.registrations?.resellersCount > 0 ? "up" : "neutral",
      link: "/resellers",
      animKey: "resellers",
    },
  ];

  const performanceData = [
    { name: "Response Time", value: 85, color: "#10b981" },
    { name: "Resolution Rate", value: 92, color: "#3b82f6" },
    { name: "Customer Satisfaction", value: 88, color: "#f59e0b" },
  ];

  const recentTickets = [
    {
      id: "LUN-001234",
      subject: "Payment gateway issue",
      customer: "John Doe",
      status: "open",
      priority: "high",
    },
    {
      id: "LUN-001235",
      subject: "Cannot access dashboard",
      customer: "Jane Smith",
      status: "answered",
      priority: "medium",
    },
    {
      id: "LUN-001236",
      subject: "Invoice correction needed",
      customer: "Bob Johnson",
      status: "closed",
      priority: "low",
    },
  ];

  // Helper function to format time ago
  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return "Unknown";

    const now = new Date();
    const time = new Date(timestamp);
    const diff = Math.floor((now - time) / 1000); // difference in seconds

    if (diff < 5) return "Just now";
    if (diff < 60) return `${diff} seconds ago`;

    const minutes = Math.floor(diff / 60);

    if (minutes === 1) return "1 minute ago";
    if (minutes < 60) return `${minutes} minutes ago`;

    const hours = Math.floor(diff / 3600);

    if (hours === 1) return "1 hour ago";
    if (hours < 24) return `${hours} hours ago`;

    const days = Math.floor(diff / 86400);

    if (days === 1) return "1 day ago";

    return `${days} days ago`;
  };

  const topCustomers = [
    { id: 1, name: "Acme Corp", revenue: "$45,230", tickets: 12, avatar: "AC" },
    {
      id: 2,
      name: "Tech Solutions",
      revenue: "$38,450",
      tickets: 8,
      avatar: "TS",
    },
    {
      id: 3,
      name: "Global Industries",
      revenue: "$32,100",
      tickets: 15,
      avatar: "GI",
    },
    {
      id: 4,
      name: "StartUp Inc",
      revenue: "$28,900",
      tickets: 6,
      avatar: "SI",
    },
  ];

  const tasks = [
    {
      id: 1,
      title: "Review pending invoices",
      due: "Today",
      priority: "high",
      completed: false,
      link: "/invoices",
    },
    {
      id: 2,
      title: "Follow up on LUN-001234",
      due: "Today",
      priority: "medium",
      completed: false,
      link: "/tickets",
    },
    {
      id: 3,
      title: "Update FAQ content",
      due: "Ongoing",
      priority: "medium",
      completed: false,
      link: "/faq-manager",
    },
    {
      id: 4,
      title: "Update customer database",
      due: "Tomorrow",
      priority: "low",
      completed: false,
      link: "/customers",
    },
    {
      id: 5,
      title: "Prepare monthly report",
      due: "This week",
      priority: "medium",
      completed: true,
      link: "/reports",
    },
  ];

  const handleLayoutChange = (_, nextLayouts) => {
    setLayouts(nextLayouts);
  };

  const visibleWidgetIds = new Set(
    (layouts[gridBreakpoint] || layouts.lg).map((item) => item.i)
  );

  // Widget renderers
  const renderWidget = (widgetId) => {
    if (loading) {
      // Return shimmer based on widget type
      switch (widgetId) {
        case "stat-active":
        case "stat-users":
        case "stat-premium":
        case "stat-revenue":
          return (
            <div
              className={`relative h-full ${isMobile ? "p-3" : "p-4"} bg-white dark:bg-gray-800/30 dark:backdrop-blur-sm flex flex-col justify-center`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 mr-2 space-y-2">
                  <Shimmer className="h-3 w-20 rounded-md" />
                  <Shimmer
                    className={`rounded-md ${isMobile ? "h-7 w-16" : "h-8 w-20"}`}
                  />
                </div>
                <Shimmer
                  className={`rounded-2xl flex-shrink-0 ml-2 ${
                    isMobile ? "w-12 h-12" : "w-14 h-14"
                  }`}
                />
              </div>
            </div>
          );
        case "revenue":
        case "categories":
        case "performance":
          return (
            <div className="dashboard-panel-surface h-full flex flex-col gap-3 p-4">
              <Shimmer className="h-4 w-32 rounded-md" />
              <Shimmer className="flex-1 rounded-xl" />
            </div>
          );
        case "tickets":
        case "activity":
        case "customers":
        case "tasks":
          return (
            <div className="p-4">
              <div className="space-y-2.5">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    className="rounded-xl bg-transparent p-3 ring-1 ring-gray-300/70 dark:bg-white/[0.04] dark:ring-white/[0.06]"
                    key={i}
                  >
                    <Shimmer className="h-3 w-3/4 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          );
        case "billing":
          return (
            <div className="dashboard-panel-surface h-full flex flex-col gap-3 p-4">
              <Shimmer className="h-4 w-24 rounded-md" />
              <div className="grid grid-cols-2 gap-2 flex-1 content-start">
                {[0, 1, 2, 3].map((i) => (
                  <div
                    className="space-y-2 rounded-xl bg-transparent p-3 ring-1 ring-gray-300/70 dark:bg-white/[0.04] dark:ring-white/[0.06]"
                    key={i}
                  >
                    <div className="flex items-center gap-2">
                      <Shimmer className="w-6 h-6 rounded-md" />
                      <Shimmer className="h-2.5 w-12 rounded-md" />
                    </div>
                    <Shimmer className="h-7 w-20 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          );
        case "staff-online":
          return (
            <div className="dashboard-panel-surface h-full flex flex-col gap-3 p-4">
              <div className="flex items-center justify-between">
                <Shimmer className="h-4 w-32 rounded-md" />
                <Shimmer className="h-5 w-16 rounded-full" />
              </div>
              <div className="grid grid-cols-3 gap-3 p-1">
                {[0, 1, 2].map((i) => (
                  <div
                    className="flex flex-col items-center rounded-xl bg-transparent p-3 ring-1 ring-gray-300/70 dark:bg-white/[0.04] dark:ring-white/[0.06]"
                    key={i}
                  >
                    <Shimmer className="w-16 h-16 rounded-full mb-2" />
                    <Shimmer className="h-3 w-3/4 rounded-md mb-1" />
                    <Shimmer className="h-2.5 w-1/2 rounded-md" />
                  </div>
                ))}
              </div>
            </div>
          );
        default:
          return (
            <div className="dashboard-panel-surface h-full p-4">
              <Shimmer className="h-full w-full rounded-xl" />
            </div>
          );
      }
    }

    // Individual stat card rendering
    const statIndex = [
      "stat-active",
      "stat-users",
      "stat-premium",
      "stat-revenue",
    ].indexOf(widgetId);

    if (statIndex !== -1) {
      const stat = stats[statIndex];

      return (
        <motion.div
          className={`relative overflow-hidden h-full cursor-pointer group ${
            isMobile ? "p-3" : "p-4"
          } bg-white dark:bg-gray-800/30 dark:backdrop-blur-sm flex flex-col justify-center`}
          whileHover={{ y: isMobile ? 0 : -2 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => navigate(stat.link)}
        >
          {/* Accent glow - top-right corner, color-matched */}
          <div
            className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-bl ${stat.accent} via-transparent to-transparent blur-2xl opacity-70 dark:opacity-60 pointer-events-none`}
          />

          <div className="flex items-center justify-between relative">
            <div className="flex-1 min-w-0 mr-2">
              <div className="flex items-center gap-1.5 min-w-0">
                <p
                  className={`${isMobile ? "text-xs" : "text-sm"} text-gray-500 dark:text-gray-400 font-medium truncate`}
                >
                  {stat.title}
                </p>
                {stat.tooltip ? (
                  <MetricInfoTooltip text={stat.tooltip} />
                ) : null}
              </div>
              <h3
                className={`${isMobile ? "text-2xl" : "text-3xl"} font-bold text-gray-900 dark:text-white truncate mt-1 tabular-nums`}
              >
                {statsLoading ? (
                  <Shimmer
                    className={`rounded-md ${isMobile ? "h-7 w-16" : "h-8 w-20"}`}
                  />
                ) : (
                  <AnimatedNumber value={stat.value} />
                )}
              </h3>
              {!statsLoading && stat.subtitle ? (
                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 truncate">
                  {stat.subtitle}
                </p>
              ) : null}
            </div>
            <div
              className={`${stat.iconHalo} rounded-2xl flex items-center justify-center flex-shrink-0 ml-2 transition-all group-hover:ring-2 ${
                isMobile ? "w-12 h-12" : "w-14 h-14"
              }`}
            >
              <i
                className={`${stat.icon} ${isMobile ? "text-lg" : "text-xl"}`}
              />
            </div>
          </div>
        </motion.div>
      );
    }

    switch (widgetId) {
      case "client-insights": {
        const isDarkMode = document.documentElement.classList.contains("dark");

        return (
          <ClientInsightsRow
            clientActivity={alignedClientActivity}
            clientActivityLoading={clientActivityLoading}
            isDarkMode={isDarkMode}
          />
        );
      }

      case "revenue": {
        const isDarkMode = document.documentElement.classList.contains("dark");

        return (
          <div className="dashboard-panel-surface flex h-full min-h-[280px] flex-col overflow-hidden p-3 sm:min-h-0 sm:p-4">
            <div className="min-h-[200px] flex-1 sm:min-h-0">
              <StackedChart isDarkMode={isDarkMode} />
            </div>
          </div>
        );
      }

      case "activity":
        return (
          <div className="dashboard-panel-surface flex h-auto flex-col overflow-hidden p-4 sm:h-full">
            <div className="flex justify-between items-center mb-3 flex-shrink-0">
              <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
                Support
              </h3>
              <button
                className="p-1.5 hover:bg-white/60 dark:hover:bg-white/[0.06] rounded-lg transition-all hover:rotate-180 duration-500"
                title="Refresh"
                onClick={() => window.location.reload()}
              >
                <i className="fas fa-sync-alt text-xs text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            {/* Ticket counters */}
            <div className="mb-3 grid flex-shrink-0 grid-cols-2 gap-2">
              <button
                className="rounded-xl p-2.5 text-left transition-all hover:brightness-110"
                style={{ backgroundColor: "#FB2C36" }}
                onClick={() => navigate("/tickets?status=awaiting-reply")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20">
                      <i className="fas fa-tag text-[11px] text-white" />
                    </div>
                    <p className="ticket-counter-label text-xs text-white">
                      Awaiting Reply
                    </p>
                  </div>
                  <p className="text-xl font-bold tabular-nums text-white">
                    <AnimatedNumber value={awaitingReplyTickets} />
                  </p>
                </div>
              </button>

              <button
                className="rounded-xl p-2.5 text-left transition-all hover:brightness-110"
                style={{ backgroundColor: "#155DFC" }}
                onClick={() => navigate("/tickets?filter=mine")}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1.5">
                    <div className="flex h-6 w-6 items-center justify-center rounded-md bg-white/20">
                      <i className="fas fa-flag text-[11px] text-white" />
                    </div>
                    <p className="ticket-counter-label text-xs text-white">
                      Assigned To You
                    </p>
                  </div>
                  <p className="text-xl font-bold tabular-nums text-white">
                    <AnimatedNumber value={assignedTickets} />
                  </p>
                </div>
              </button>
            </div>

            {/* Recent tickets */}
            <div className="mb-3 flex min-h-0 flex-1 flex-col overflow-hidden rounded-xl bg-transparent ring-1 ring-gray-300/70 dark:bg-white/[0.02] dark:ring-white/[0.06]">
              <div className="flex-1 min-h-0 overflow-auto">
                {ticketsData.length > 0 ? (
                  ticketsData.slice(0, 3).map((ticket, index) => (
                    <ListItemMotion
                      className="border-b border-gray-200/60 dark:border-white/[0.05] last:border-b-0"
                      index={index}
                      key={index}
                    >
                      <button
                        className="w-full group px-3 py-2 hover:bg-gray-50 dark:hover:bg-white/[0.04] transition-all text-left"
                        onClick={() =>
                          navigate(`/tickets/${ticket.ticketNumber}`)
                        }
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <div className="flex min-w-0 flex-1 items-center gap-2 overflow-hidden">
                            <span className="shrink-0 whitespace-nowrap font-mono text-[11px] font-medium text-blue-600 dark:text-blue-400">
                              {ticket.ticketNumber}
                            </span>
                            <span className="min-w-0 truncate text-xs text-gray-700 dark:text-gray-300">
                              {ticket.subject}
                            </span>
                          </div>
                          <span className="shrink-0 whitespace-nowrap text-[11px] text-gray-500 dark:text-gray-400">
                            {ticket.timeAgo}
                          </span>
                        </div>
                      </button>
                    </ListItemMotion>
                  ))
                ) : (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-white/[0.05] flex items-center justify-center mr-2">
                      <i className="fas fa-inbox text-sm text-gray-400" />
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      No recent tickets
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Action buttons */}
            <div className="grid flex-shrink-0 grid-cols-3 gap-1.5">
              <button
                className="flex items-center justify-center gap-1.5 rounded-lg bg-transparent px-2 py-1.5 text-[11px] font-medium text-gray-700 ring-1 ring-gray-300/70 transition-all hover:bg-gray-100/60 hover:ring-gray-400 dark:bg-white/[0.04] dark:text-gray-300 dark:ring-white/[0.06] dark:hover:bg-white/[0.08] dark:hover:ring-white/[0.12]"
                onClick={() => navigate("/tickets")}
              >
                <i className="fas fa-list text-xs" />
                <span>View All</span>
              </button>
              <button
                className="flex items-center justify-center gap-1.5 rounded-lg bg-transparent px-2 py-1.5 text-[11px] font-medium text-gray-700 ring-1 ring-gray-300/70 transition-all hover:bg-gray-100/60 hover:ring-gray-400 dark:bg-white/[0.04] dark:text-gray-300 dark:ring-white/[0.06] dark:hover:bg-white/[0.08] dark:hover:ring-white/[0.12]"
                onClick={() => navigate("/tickets?filter=mine")}
              >
                <i className="fas fa-user text-xs" />
                <span>View Mine</span>
              </button>
              <button
                className="flex items-center justify-center gap-1.5 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 px-2 py-1.5 text-[11px] font-semibold text-white shadow-blue-500/30 transition-all hover:from-blue-600 hover:to-blue-700 hover:shadow-lg"
                onClick={() => navigate("/tickets?action=new")}
              >
                <i className="fas fa-plus text-xs" />
                <span>New Ticket</span>
              </button>
            </div>
          </div>
        );

      case "tickets":
        return (
          <div className="dashboard-panel-surface flex h-full flex-col p-4">
            <div className="flex-1 overflow-auto">
              {recentTickets.map((ticket, index) => (
                <div key={index}>
                  <div
                    className="py-2 hover:bg-gray-50 dark:hover:bg-gray-700/50/50 transition-colors cursor-pointer px-2 -mx-2 rounded"
                    onClick={() => navigate(`/tickets/${ticket.id}`)}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-primary-600">
                          {ticket.id}
                        </span>
                        <span
                          className={`badge ${
                            ticket.priority === "high"
                              ? "badge-danger"
                              : ticket.priority === "medium"
                                ? "badge-warning"
                                : "badge-success"
                          } text-xs`}
                        >
                          {ticket.priority}
                        </span>
                      </div>
                      <span
                        className={`badge ${
                          ticket.status === "open"
                            ? "badge-info"
                            : ticket.status === "customer-replied"
                              ? "badge-warning"
                              : ticket.status === "answered"
                                ? "badge-secondary"
                                : "badge-default"
                        } text-xs`}
                      >
                        {ticket.status}
                      </span>
                    </div>
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                      {ticket.subject}
                    </p>
                  </div>
                  {index < recentTickets.length - 1 && (
                    <div className="border-b border-gray-200 dark:border-gray-700" />
                  )}
                </div>
              ))}
            </div>
            <button
              className="w-full mt-3 btn btn-primary btn-sm justify-center hover:shadow-md transition-all"
              onClick={() => navigate("/tickets")}
            >
              <i className="fas fa-arrow-right mr-2" />
              View All Tickets
            </button>
          </div>
        );

      case "categories":
        return (
          <div className="dashboard-panel-surface h-full min-h-[320px] overflow-hidden p-3 sm:min-h-0 sm:p-4">
            <ClientActivityPanel
              showHeading
              clientActivity={alignedClientActivity}
              loading={clientActivityLoading}
            />
          </div>
        );

      case "client-map": {
        const isDarkMode = document.documentElement.classList.contains("dark");

        return (
          <div className="dashboard-panel-surface flex h-full min-h-[320px] flex-col overflow-hidden p-3 sm:p-4">
            <div className="mb-2 flex flex-shrink-0 items-start justify-between gap-2 sm:mb-3">
              <div className="min-w-0">
                <h3 className="text-sm font-semibold tracking-tight text-gray-900 dark:text-white sm:text-base">
                  Live Client Map
                </h3>
                <p className="mt-0.5 text-[11px] leading-snug text-gray-500 dark:text-gray-400 sm:text-xs">
                  <span className="sm:hidden">
                    Live heartbeat clients by city. Green = authenticated, blue
                    = no-auth.
                  </span>
                  <span className="hidden sm:inline">
                    The same live heartbeat clients shown in the headline,
                    clustered by approximate city-level location. Unmapped
                    clients are reported separately.
                  </span>
                </p>
              </div>
            </div>
            <div className="h-[280px] w-full flex-1 sm:h-[360px] lg:min-h-[280px]">
              <LiveUserMap
                data={clientActivityGeoJson}
                isDarkMode={isDarkMode}
                loading={clientMapLoading}
              />
            </div>
          </div>
        );
      }

      case "performance":
        const performanceChartOptions = {
          chart: {
            type: "radialBar",
            height: "100%",
            sparkline: {
              enabled: true,
            },
          },
          plotOptions: {
            radialBar: {
              startAngle: -90,
              endAngle: 90,
              track: {
                background: "#e7e7e7",
                strokeWidth: "97%",
                margin: 5,
                dropShadow: {
                  enabled: false,
                },
              },
              dataLabels: {
                name: {
                  show: true,
                  fontSize: "14px",
                  offsetY: -10,
                },
                value: {
                  offsetY: 5,
                  fontSize: "20px",
                  formatter(val) {
                    return `${val}%`;
                  },
                },
              },
            },
          },
          grid: {
            padding: {
              top: -10,
            },
          },
          fill: {
            type: "gradient",
            gradient: {
              shade: "light",
              shadeIntensity: 0.4,
              inverseColors: false,
              opacityFrom: 1,
              opacityTo: 1,
              stops: [0, 50, 53, 91],
            },
          },
          labels: performanceData.map((d) => d.name),
          series: performanceData.map((d) => d.value),
          colors: performanceData.map((d) => d.color),
        };

        return (
          <div className="dashboard-panel-surface flex h-full flex-col p-4">
            <div className="flex-1" style={{ minHeight: "200px" }}>
              <Chart
                height="100%"
                options={performanceChartOptions}
                series={performanceChartOptions.series}
                type="radialBar"
              />
            </div>
          </div>
        );

      case "customers":
        return (
          <div className="dashboard-panel-surface flex h-full flex-col p-4">
            <div>
              {topCustomers.map((customer, index) => (
                <div key={index}>
                  <div
                    className="flex items-center justify-between py-3 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50/50 transition-colors cursor-pointer"
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                        {customer.avatar}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                          {customer.name}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {customer.tickets} tickets
                        </p>
                      </div>
                    </div>
                    <span className="text-sm font-bold text-gray-900 dark:text-white">
                      {customer.revenue}
                    </span>
                  </div>
                  {index < topCustomers.length - 1 && (
                    <div className="border-b border-gray-200 dark:border-gray-700" />
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case "tasks":
        return (
          <div className="dashboard-panel-surface flex h-full flex-col p-4">
            <div>
              {tasks.map((task, index) => (
                <div key={task.id}>
                  <div
                    className="flex items-center gap-3 py-3 px-2 -mx-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50/50 transition-colors cursor-pointer"
                    onClick={() => task.link && navigate(task.link)}
                  >
                    <input
                      checked={task.completed}
                      className="rounded border-gray-300 text-primary-600"
                      type="checkbox"
                      onChange={(e) => {
                        e.stopPropagation();
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                    <div className="flex-1">
                      <p
                        className={`text-sm ${task.completed ? "line-through text-gray-400" : "text-gray-900 dark:text-white"}`}
                      >
                        {task.title}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {task.due}
                      </p>
                    </div>
                    <span
                      className={`badge text-xs ${
                        task.priority === "high"
                          ? "badge-danger"
                          : task.priority === "medium"
                            ? "badge-warning"
                            : "badge-success"
                      }`}
                    >
                      {task.priority}
                    </span>
                  </div>
                  {index < tasks.length - 1 && (
                    <div className="border-b border-gray-200 dark:border-gray-700" />
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case "billing":
        return (
          <div className="dashboard-panel-surface flex h-auto flex-col overflow-hidden p-4 sm:h-full">
            <div className="flex justify-between items-center mb-3 flex-shrink-0">
              <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
                Earnings
              </h3>
              <button
                className="p-1.5 hover:bg-white/60 dark:hover:bg-white/[0.06] rounded-lg transition-all"
                title="View All Invoices"
                onClick={() => navigate("/invoices")}
              >
                <i className="fas fa-external-link-alt text-xs text-gray-600 dark:text-gray-400" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-2 flex-1 content-start">
              {[
                {
                  label: "Today",
                  icon: "fas fa-calendar-day",
                  iconColor: "text-green-600 dark:text-green-400",
                  iconBg: "bg-green-500/10 ring-green-500/20",
                  value: billingData.today,
                  href: "/invoices?period=today",
                },
                {
                  label: "This Month",
                  icon: "fas fa-calendar-alt",
                  iconColor: "text-blue-600 dark:text-blue-400",
                  iconBg: "bg-blue-500/10 ring-blue-500/20",
                  value: billingData.thisMonth,
                  href: "/invoices?period=month",
                },
                {
                  label: "This Year",
                  icon: "fas fa-chart-line",
                  iconColor: "text-purple-600 dark:text-purple-400",
                  iconBg: "bg-purple-500/10 ring-purple-500/20",
                  value: billingData.thisYear,
                  href: "/invoices?period=year",
                },
                {
                  label: "All Time",
                  icon: "fas fa-trophy",
                  iconColor: "text-pink-600 dark:text-pink-400",
                  iconBg: "bg-pink-500/10 ring-pink-500/20",
                  value: billingData.allTime,
                  href: "/invoices",
                },
              ].map((card, index) => (
                <ListItemMotion
                  className="rounded-xl bg-transparent ring-1 ring-gray-300/70 dark:bg-white/[0.04] dark:ring-white/[0.06]"
                  index={index}
                  key={card.label}
                >
                  <button
                    className="w-full rounded-xl p-3 text-left transition-all hover:bg-gray-100/50 hover:ring-gray-400 dark:hover:bg-white/[0.06] dark:hover:ring-white/[0.12]"
                    onClick={() => navigate(card.href)}
                  >
                    <div className="flex items-center gap-2 mb-1.5">
                      <div
                        className={`w-6 h-6 rounded-md flex items-center justify-center ring-1 ${card.iconBg}`}
                      >
                        <i
                          className={`${card.icon} ${card.iconColor} text-[11px]`}
                        />
                      </div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">
                        {card.label}
                      </p>
                    </div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
                      {billingLoading ? (
                        <Shimmer className="h-7 w-20 rounded-md" />
                      ) : (
                        <AnimatedNumber
                          format={formatCurrency}
                          value={card.value}
                        />
                      )}
                    </p>
                  </button>
                </ListItemMotion>
              ))}
            </div>
          </div>
        );
      case "staff-online": {
        const onlineAdmins = staffOnline.filter((s) => s.isOnline).length;

        return (
          <div className="dashboard-panel-surface flex h-auto flex-col overflow-hidden p-4 sm:h-full">
            <div className="mb-3 flex flex-shrink-0 items-center justify-between">
              <h3 className="text-base font-semibold tracking-tight text-gray-900 dark:text-white">
                Staff Members
              </h3>
              <div className="flex items-center gap-1.5 rounded-full bg-green-500/10 px-2 py-1 ring-1 ring-green-500/20">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-500 opacity-60" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />
                </span>
                <span className="text-[11px] font-medium text-green-700 dark:text-green-400">
                  {onlineAdmins} online
                </span>
              </div>
            </div>

            <div className="min-h-0 flex-1 overflow-hidden">
              {staffOnlineLoading ? (
                <div className="grid grid-cols-3 gap-3 p-1">
                  {[0, 1, 2].map((i) => (
                    <div className="flex flex-col items-center p-1" key={i}>
                      <Shimmer className="mb-2 h-[92px] w-[92px] rounded-full" />
                      <Shimmer className="mb-1 h-3 w-3/4 rounded-md" />
                      <Shimmer className="h-2.5 w-1/2 rounded-md" />
                    </div>
                  ))}
                </div>
              ) : staffOnline.length > 0 ? (
                <div className="grid grid-cols-3 gap-3 overflow-visible p-1">
                  {staffOnline.map((staff, index) => (
                    <ListItemMotion
                      className={`relative z-10 flex flex-col items-center p-1 ${
                        !staff.isOnline ? "opacity-50" : ""
                      }`}
                      index={index}
                      key={staff.id || index}
                    >
                      <div className="relative mb-2">
                        {staff.avatar ? (
                          <img
                            alt={staff.name}
                            className="h-[92px] w-[92px] rounded-full object-cover ring-2 ring-gray-200 dark:ring-white/10"
                            src={staff.avatar}
                            onError={(e) => {
                              e.target.style.display = "none";
                              e.target.nextSibling.style.display = "flex";
                            }}
                          />
                        ) : null}
                        <div
                          className={`h-[92px] w-[92px] items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-xl font-bold text-white ${staff.avatar ? "hidden" : "flex"}`}
                          style={staff.avatar ? { display: "none" } : {}}
                        >
                          {staff.name?.charAt(0).toUpperCase() ||
                            staff.username?.charAt(0).toUpperCase() ||
                            "A"}
                        </div>
                        {staff.isOnline && (
                          <div className="absolute bottom-0 right-0 h-4 w-4 rounded-full bg-green-500 ring-2 ring-white dark:ring-gray-900" />
                        )}
                      </div>
                      <p className="w-full truncate text-center text-xs font-medium text-gray-900 dark:text-white">
                        {staff.name || staff.username}
                      </p>
                      <p className="text-center text-[11px] text-gray-500 dark:text-gray-400">
                        {staff.isOnline
                          ? staff.email === user.email
                            ? "Online now"
                            : formatTimeAgo(staff.lastActive)
                          : `Last seen ${formatTimeAgo(staff.lastActive)}`}
                      </p>
                      {(() => {
                        const { ticketsAnswered = 0, publicReplies = 0 } =
                          staff.activity || {};

                        return (
                          <div className="mt-1 flex flex-col items-center gap-0.5 text-[11px] text-gray-500 dark:text-gray-400">
                            <span>
                              {ticketsAnswered === 1
                                ? "1 ticket answered"
                                : `${ticketsAnswered} tickets answered`}
                            </span>
                            <span>
                              {publicReplies === 1
                                ? "1 public reply"
                                : `${publicReplies} public replies`}
                            </span>
                          </div>
                        );
                      })()}
                    </ListItemMotion>
                  ))}
                </div>
              ) : (
                <div className="flex h-full flex-col items-center justify-center">
                  <div className="mb-3 flex h-[92px] w-[92px] items-center justify-center rounded-full bg-gray-100 dark:bg-white/[0.05]">
                    <i className="fas fa-user-slash text-2xl text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    No staff members found
                  </p>
                </div>
              )}
            </div>

            <p className="mt-3 flex-shrink-0 text-center text-[11px] leading-relaxed text-gray-500 dark:text-gray-400">
              {onlineAdmins === 0
                ? "No admins are online right now."
                : onlineAdmins === 1
                  ? "This is the current admin online."
                  : `These are the ${onlineAdmins} admins currently online.`}
            </p>
          </div>
        );
      }
      default:
        return null;
    }
  };

  return (
    <div className={`${isMobile ? "space-y-0" : "space-y-6"} relative`}>
      {/* Portrait mode stat widgets - 2x2 grid */}
      {isMobile && !isLandscape && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gridTemplateRows: "auto auto",
              gap: "4px",
              padding: "4px",
              marginBottom: "4px",
            }}
          >
            {["stat-active", "stat-users", "stat-premium", "stat-revenue"]
              .filter((id) => !hiddenWidgets.includes(id))
              .map((widgetId) => (
                <div
                  className="card overflow-hidden shadow-md stat-widget"
                  key={widgetId}
                  style={{
                    height: "90px",
                  }}
                >
                  <div className="h-full overflow-hidden bg-white dark:bg-gray-800/30 dark:backdrop-blur-sm">
                    {renderWidget(widgetId)}
                  </div>
                </div>
              ))}
          </div>

          {/* Earnings → Support → Staff, then insights / map */}
          {!hiddenWidgets.includes("billing") && (
            <div
              className="overflow-visible bg-transparent"
              style={{ margin: "4px", marginBottom: "8px" }}
            >
              <div className="bg-transparent">{renderWidget("billing")}</div>
            </div>
          )}

          {!hiddenWidgets.includes("activity") && (
            <div
              className="overflow-visible bg-transparent"
              style={{ margin: "4px", marginBottom: "8px" }}
            >
              <div className="bg-transparent">{renderWidget("activity")}</div>
            </div>
          )}

          {!hiddenWidgets.includes("staff-online") && (
            <div
              className="overflow-visible bg-transparent"
              style={{ margin: "4px", marginBottom: "8px" }}
            >
              <div className="bg-transparent">
                {renderWidget("staff-online")}
              </div>
            </div>
          )}

          {!hiddenWidgets.includes("revenue") && (
            <div
              className="overflow-visible bg-transparent"
              style={{
                margin: "4px",
                marginBottom: "8px",
                minHeight: "320px",
              }}
            >
              <div className="bg-transparent" style={{ minHeight: "320px" }}>
                {renderWidget("revenue")}
              </div>
            </div>
          )}

          {!hiddenWidgets.includes("categories") && (
            <div
              className="overflow-visible bg-transparent"
              style={{
                margin: "4px",
                marginBottom: "8px",
                minHeight: "360px",
              }}
            >
              <div className="bg-transparent" style={{ minHeight: "360px" }}>
                {renderWidget("categories")}
              </div>
            </div>
          )}

          {!hiddenWidgets.includes("client-map") && (
            <div
              className="overflow-visible bg-transparent"
              style={{
                margin: "4px",
                marginBottom: "8px",
                height: "400px",
                minHeight: "400px",
              }}
            >
              <div
                className="bg-transparent"
                style={{ height: "100%", minHeight: "400px" }}
              >
                {renderWidget("client-map")}
              </div>
            </div>
          )}
        </>
      )}

      {/* Landscape mode stat widgets - render outside grid */}
      {isLandscape && (
        <div
          style={{
            display: "flex",
            width: "100%",
            padding: "4px",
            gap: "4px",
            marginBottom: "8px",
          }}
        >
          {["stat-active", "stat-users", "stat-premium", "stat-revenue"]
            .filter((id) => !hiddenWidgets.includes(id))
            .map((widgetId) => (
              <div
                className="card overflow-hidden shadow-md stat-widget"
                key={widgetId}
                style={{
                  flex: "1",
                  height: "96px",
                  minWidth: "0",
                }}
              >
                <div className="h-full overflow-hidden bg-white dark:bg-gray-800/30 dark:backdrop-blur-sm">
                  {renderWidget(widgetId)}
                </div>
              </div>
            ))}
        </div>
      )}

      {/* Portrait mobile renders the full stack above; skip the grid so
          react-grid-layout doesn't reserve empty height from unused rows. */}
      {!(isMobile && !isLandscape) && (
        <ResponsiveGridLayout
          breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480 }}
          className={`layout ${isMobile ? "mobile-layout" : ""}`}
          cols={{ lg: 12, md: 6, sm: 4, xs: 4 }}
          compactType="vertical"
          containerPadding={window.innerWidth < 768 ? [4, 4] : [0, 0]}
          draggableHandle=".drag-handle"
          isDraggable={false}
          isResizable={false}
          key={layoutKey}
          layouts={layouts}
          margin={
            isLandscape ? [6, 6] : window.innerWidth < 768 ? [4, 4] : [16, 16]
          }
          preventCollision={false}
          rowHeight={isLandscape ? 70 : isMobile ? 80 : 60}
          transformScale={1}
          useCSSTransforms={!isMobile || isLandscape}
          onBreakpointChange={setGridBreakpoint}
          onDragStart={() => setIsDragging(true)}
          onDragStop={() => setIsDragging(false)}
          onLayoutChange={handleLayoutChange}
          onResizeStart={() => setIsDragging(true)}
          onResizeStop={() => setIsDragging(false)}
        >
          {widgets
            .filter((widget) => visibleWidgetIds.has(widget.id))
            .filter((widget) => !hiddenWidgets.includes(widget.id))
            .filter(
              (widget) =>
                !(isMobile || isLandscape) || !widget.id.startsWith("stat-")
            )
            .filter(
              (widget) =>
                !["customers", "tasks", "performance"].includes(widget.id)
            )
            .map((widget) => {
              const isStatWidget = widget.id.startsWith("stat-");
              // Panel widgets own their background via .dashboard-panel-surface.
              // Keep outer .card / gray-800 layers off so they don't overwrite it.
              const isPanelWidget = !isStatWidget;

              return (
                <div
                  className={`${
                    isPanelWidget
                      ? "relative h-full overflow-visible group dashboard-panel-widget bg-transparent border-0 shadow-none"
                      : `card overflow-hidden transition-all duration-200 shadow-md hover:shadow-xl ${
                          isDragging ? "shadow-2xl scale-[1.02] z-50" : ""
                        } h-full relative group stat-widget`
                  }`}
                  key={widget.id}
                >
                  {/* Resize and close functionality disabled */}

                  {/* Widget content */}
                  <div
                    className={`h-full ${
                      [
                        "stat-active",
                        "stat-users",
                        "stat-premium",
                        "stat-revenue",
                        "revenue",
                        "categories",
                        "client-insights",
                        "client-map",
                      ].includes(widget.id)
                        ? "overflow-hidden"
                        : "overflow-auto"
                    } ${
                      isPanelWidget
                        ? "bg-transparent"
                        : "bg-white dark:bg-gray-800/30 dark:backdrop-blur-sm"
                    }`}
                  >
                    {renderWidget(widget.id)}
                  </div>
                </div>
              );
            })}
        </ResponsiveGridLayout>
      )}

      <AnimatePresence>
        {isDragging && (
          <motion.div
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black/10 pointer-events-none z-40"
            exit={{ opacity: 0 }}
            initial={{ opacity: 0 }}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default AdminDashboard;
